import { useT, type TranslateFn } from '@/i18n/LanguageContext';
import type { TranslationKey } from '@/i18n/dictionaries/en';

/**
 * Translates raw DB/domain enums into user-facing language and a colour
 * tone. Never render a raw enum directly in a table cell - always through
 * this. Subset of apps/ops/src/components/StatusBadge.tsx's label map -
 * only the states the seller-facing screens actually render. Labels live
 * in the i18n dictionary (`status.<ENUM_VALUE>`).
 */
const LABEL_KEYS: Record<string, TranslationKey> = {
  // inbound_shipment.state the supplies screens show (the others are mapped below)
  EXPECTED: 'status.EXPECTED',
  DRAFT: 'status.DRAFT',
  BUILDING: 'status.BUILDING',
  PICKING: 'status.PICKING',
  PICKED: 'status.PICKED',
  PACKING: 'status.PACKING',
  PACKED: 'status.PACKED',
  READY: 'status.READY',
  SHIPPED: 'status.SHIPPED',
  CANCELLED: 'status.CANCELLED',
  CANCELLED_AFTER_PICK: 'status.CANCELLED_AFTER_PICK',
  NEW: 'status.NEW',
  RESERVED: 'status.RESERVED',
  ARRIVED: 'status.ARRIVED',
  RECEIVING: 'status.RECEIVING',
  RECEIVED: 'status.RECEIVED',
  CLOSED: 'status.CLOSED',
  NONE: 'status.NONE',
  SHORT: 'status.SHORT',
  OVER: 'status.OVER',
  DAMAGED: 'status.DAMAGED',
  MISMATCH: 'status.MISMATCH',
  RESTOCK: 'status.RESTOCK',
  QUARANTINE: 'status.QUARANTINE',
  WRITE_OFF: 'status.WRITE_OFF',
  AWAITING_DISPOSITION: 'status.AWAITING_DISPOSITION',
  ISSUED: 'status.ISSUED',
  PAID: 'status.PAID',
  PARTIALLY_PAID: 'status.PARTIALLY_PAID',
  OVERDUE: 'status.OVERDUE',
  SENT: 'status.SENT',
  DISPUTED: 'status.DISPUTED',
  VOID: 'status.VOID',
  // billable_event.kind (service_catalog_item codes)
  RECEIVING_UNIT: 'status.RECEIVING_UNIT',
  RECEIVING_DOC: 'status.RECEIVING_DOC',
  PICKING_UNIT: 'status.PICKING_UNIT',
  PACKING_UNIT: 'status.PACKING_UNIT',
  PACKAGE_MATERIAL: 'status.PACKAGE_MATERIAL',
  PALLET_HANDLING: 'status.PALLET_HANDLING',
  STORAGE_LOCATION: 'status.STORAGE_LOCATION',
  RETURN_UNIT: 'status.RETURN_UNIT',
  MARKING_SCAN: 'status.MARKING_SCAN',
  RELABEL: 'status.RELABEL',
  DISPOSAL: 'status.DISPOSAL',
  MANUAL_HANDLING: 'status.MANUAL_HANDLING',
  // evidence.kind
  RECEIVING_DISCREPANCY: 'status.RECEIVING_DISCREPANCY',
  DAMAGE: 'status.DAMAGE',
  UNKNOWN_ITEM: 'status.UNKNOWN_ITEM',
  RETURN_INSPECTION: 'status.RETURN_INSPECTION',
  // evidence.entity_kind
  INBOUND_LINE: 'status.INBOUND_LINE',
  SALES_ORDER: 'status.SALES_ORDER',
  RETURN_DISPOSITION: 'status.RETURN_DISPOSITION',
  PACKAGE: 'status.PACKAGE',
  // invoice_payment.method
  BANK_TRANSFER: 'status.BANK_TRANSFER',
  CARD: 'status.CARD',
  CASH: 'status.CASH',
  OTHER: 'status.OTHER',
  // shipment.state (SHIPMENT_STATES)
  DEPARTED: 'status.DEPARTED',
  ACCEPTED: 'status.ACCEPTED',
  REJECTED: 'status.REJECTED',
};

const TONE: Record<string, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  SHIPPED: 'ok', RECEIVED: 'ok', CLOSED: 'ok', RESTOCK: 'ok', READY: 'ok', PAID: 'ok',
  CANCELLED: 'danger', CANCELLED_AFTER_PICK: 'danger', SHORT: 'danger', DAMAGED: 'danger',
  MISMATCH: 'danger', OVERDUE: 'danger', DISPUTED: 'danger', VOID: 'neutral',
  OVER: 'warn', WRITE_OFF: 'warn', AWAITING_DISPOSITION: 'warn', PARTIALLY_PAID: 'warn',
  DRAFT: 'neutral', BUILDING: 'neutral', NONE: 'neutral', NEW: 'neutral', ISSUED: 'warn', SENT: 'warn',
  DEPARTED: 'ok', ACCEPTED: 'ok', REJECTED: 'danger',
  EXPECTED: 'neutral', ARRIVED: 'warn', RECEIVING: 'warn', RESERVED: 'neutral',
};

/**
 * The seller's outbound requests/orders in the words they use: draft / sent to the warehouse / reserved /
 * picking / packed / shipped / cancelled. (The generic `status.*` labels stay for the other screens.)
 */
const OUT_STATUS_KEYS: Record<string, TranslationKey> = {
  DRAFT: 'outStatus.DRAFT',
  NEW: 'outStatus.NEW',
  RESERVED: 'outStatus.RESERVED',
  PICKING: 'outStatus.PICKING',
  PICKED: 'outStatus.PICKED',
  PACKED: 'outStatus.PACKED',
  SHIPPED: 'outStatus.SHIPPED',
  CANCELLED: 'outStatus.CANCELLED',
  CANCELLED_AFTER_PICK: 'outStatus.CANCELLED_AFTER_PICK',
  ON_HOLD: 'outStatus.ON_HOLD',
};

export function outboundStatusLabel(t: TranslateFn, raw: string | null | undefined): string {
  if (!raw) return '—';
  const key = OUT_STATUS_KEYS[raw];
  return key ? t(key) : statusLabel(t, raw);
}

export function OutboundStatusBadge({ value }: { value: string | null | undefined }): JSX.Element {
  const t = useT();
  if (!value) return <span className="badge neutral">—</span>;
  return <span className={`badge ${TONE[value] ?? 'neutral'}`}>{outboundStatusLabel(t, value)}</span>;
}

export function statusLabel(t: TranslateFn, raw: string | null | undefined): string {
  if (!raw) return '—';
  const key = LABEL_KEYS[raw];
  return key ? t(key) : raw.toLowerCase().replace(/_/g, ' ');
}

export function StatusBadge({ value }: { value: string | null | undefined }): JSX.Element {
  const t = useT();
  if (!value) return <span className="badge neutral">—</span>;
  const tone = TONE[value] ?? 'neutral';
  return <span className={`badge ${tone}`}>{statusLabel(t, value)}</span>;
}
